import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo } from 'react';
import {
  combineActionedCount,
  computeProgressPercent,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/utils/task-progress';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_SPRING_CONFIG_INTRO } from '@experiences/blocs/utils/animations';
import {
  PricingRecommendationRow,
  PublishedProgress,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import { RecommendationAction } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { SegmentRing } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/segment-ring';
import { shadow } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLocalActionCounts } from '@/experiences/sales-portal/domain/pricing-agent/blocs/hooks/use-local-action-counts';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';
import { View } from 'react-native';

interface Props {
  defaultAccepted?: boolean;
  publishedProgress: PublishedProgress;
  rows?: PricingRecommendationRow[];
}

export const AgentReviewFooter: React.FC<Props> = ({
  defaultAccepted = false,
  publishedProgress,
  rows = [],
}) => {
  const [styles, theme] = useStyles();

  const { acceptedCount, actionedCount, overriddenCount, rejectedCount } =
    useLocalActionCounts();
  const actions = useRecommendationActionsStore((state) => state.actions);

  const {
    acceptedDisplayCount,
    overriddenDisplayCount,
    progressPercent,
    publishedActionedCount,
    rejectedDisplayCount,
    ringActionedCount,
  } = useMemo(() => {
    // Auto-publish: every row is accepted by default and nothing is published
    // yet, so the ring is always full — tally each row's effective status (the
    // user's edit where there is one, otherwise the backend status).
    if (defaultAccepted) {
      let accepted = 0;
      let rejected = 0;
      let overridden = 0;
      for (const row of rows) {
        const action = actions[row.uuid]?.action ?? row.reviewStatus;
        if (action === RecommendationAction.Overridden) {
          overridden += 1;
        } else if (action === RecommendationAction.Rejected) {
          rejected += 1;
        } else {
          accepted += 1;
        }
      }
      return {
        acceptedDisplayCount: accepted,
        overriddenDisplayCount: overridden,
        progressPercent: rows.length === 0 ? 0 : 100,
        publishedActionedCount: 0,
        rejectedDisplayCount: rejected,
        ringActionedCount: rows.length,
      };
    }

    const {
      accepted: publishedAccepted,
      overridden: publishedOverridden,
      pending: publishedPending,
      rejected: publishedRejected,
    } = publishedProgress;
    const allRecommendationsCount =
      publishedAccepted +
      publishedOverridden +
      publishedPending +
      publishedRejected;
    const publishedRecommendationsCount =
      publishedAccepted + publishedOverridden + publishedRejected;
    const combinedActionedRecommendationsCount = combineActionedCount(
      publishedRecommendationsCount,
      actionedCount,
      allRecommendationsCount,
    );

    return {
      acceptedDisplayCount: acceptedCount,
      overriddenDisplayCount: overriddenCount,
      progressPercent: computeProgressPercent(
        combinedActionedRecommendationsCount,
        allRecommendationsCount,
      ),
      publishedActionedCount: publishedRecommendationsCount,
      rejectedDisplayCount: rejectedCount,
      ringActionedCount: combinedActionedRecommendationsCount,
    };
  }, [
    acceptedCount,
    actionedCount,
    actions,
    defaultAccepted,
    overriddenCount,
    publishedProgress,
    rejectedCount,
    rows,
  ]);

  const translateY = useSharedValue(100);
  const opacity = useSharedValue(0);
  useEffect(() => {
    translateY.value = withSpring(0, DEFAULT_SPRING_CONFIG_INTRO);
    opacity.value = withSpring(1, DEFAULT_SPRING_CONFIG_INTRO);
  }, [translateY, opacity]);
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      <View style={styles.shadowContainer}>
        <View style={styles.content}>
          <SegmentRing
            acceptedCount={acceptedDisplayCount}
            actionedCount={ringActionedCount}
            overriddenCount={overriddenDisplayCount}
            progressPercent={progressPercent}
            publishedCount={publishedActionedCount}
          />
          <View style={styles.separator} />
          <CountCell
            color={theme.sk.color.text.primary}
            count={publishedActionedCount}
            label={i18n.t('pricing.agent.review.status.published')}
          />
          <View style={styles.separator} />
          <CountCell
            color={theme.sk.color.text.success}
            count={acceptedDisplayCount}
            label={i18n.t('pricing.agent.review.status.accepted')}
          />
          <View style={styles.separator} />
          <CountCell
            color={theme.sk.color.text.error}
            count={rejectedDisplayCount}
            label={i18n.t('pricing.agent.review.status.rejected')}
          />
          <View style={styles.separator} />
          <CountCell
            color={theme.sk.color.text.blue}
            count={overriddenDisplayCount}
            label={i18n.t('pricing.agent.review.status.overridden')}
          />
        </View>
      </View>
    </Animated.View>
  );
};

interface CountCellProps {
  color: string;
  count: number;
  label: string;
}

const CountCell: React.FC<CountCellProps> = ({ color, count, label }) => {
  const [styles, theme] = useStyles();
  return (
    <View style={styles.metric}>
      <Typography color={theme.sk.color.text.secondary} variant="label-sm">
        {label}
      </Typography>
      <Typography color={color} variant="paragraph-md-600">
        {count}
      </Typography>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    alignSelf: 'center',
    bottom: 0,
    position: 'absolute',
  },
  content: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  metric: {
    alignItems: 'center',
    gap: 2,
  },
  separator: {
    alignSelf: 'stretch',
    backgroundColor: theme.sk.color.border.primary,
    width: 1,
  },
  shadowContainer: {
    ...shadow({
      opacity: 0.12,
      radius: 24,
      x: 0,
      y: 12,
    }),
    borderRadius: theme.sk.radii.radiusLarge,
    marginBottom: theme.sk.spacing.spaceLarge,
    overflow: 'hidden',
  },
}));
